import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { t } from 'i18next';
import { useHistory } from 'react-router';
import { Trans } from 'react-i18next';
import { useDidComponentMount } from '@hooks/use-did-component-mount';

import { Container } from '@common/components/container';
import { Overview } from '@common/components/overview';
import Bar from '@common/components/bar';
import DataTable from '@common/components/datatable';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { EPredicateFilters } from '@common/components/predicates-filter/predicates-filter';
import { useFiltersDefaultData } from '@common/components/audience-filters-preview';

import { useAppSelector } from '@common/hooks';
import { usePredicateState } from '@common/hooks/use-predicate-state';
import { useIsAvailableInPlanPackage } from '@common/hooks/use-is-available-in-plan-package';
import { useEncodedFullPath } from '@common/hooks/url';
import Api, { useRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';
import Placeholder from '@common/components/placeholder/placeholder';
import Alert from '@common/components/alert';
import { Form } from '@modules/forms/types';

import { EComponentTypes, EPlanPackageConfig, EPredicateFields } from '@common/definitions';
import { selected } from '@modules/organisation/selectors/organisation';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import { TemplateLibrary } from '@modules/forms/components/template-library';

import FormsTopNavigation from '../../components/forms/FormsTopNavigation';
import FormsSubNavigation from '../../components/forms/FormsSubNavigation';
import FormTemplates from '../../components/forms/FormTemplates';
import { FormsProps, FormsMeta } from './types';
import useColumns from './useColumns';
import { CreateTemplateForm } from '@modules/forms/forms/create-template';
import { omit } from 'lodash';

export const FormsContainer = (props: FormsProps) => {
  const searchParams = new URLSearchParams(props.location.search);
  const [isTemplatesOpen, setTemplatesOpen] = useState(!!searchParams.get('templates'));
  const [search, setSearch] = useState<string>('');

  const organisation = useAppSelector(selected);
  const { match } = props;
  const didMount = useDidComponentMount();
  const history = useHistory();
  const encodedPath = useEncodedFullPath();
  const networks = useAppSelector(organisationSelectors.networks);
  const functions = useAppSelector(organisationSelectors.functions);
  const [predicates, setPredicates] = usePredicateState({
    networks,
    functions,
  });
  const [audiencePreviewData, setMetas]: any = useFiltersDefaultData();
  const [templateFormId, setTemplateFormId] = useState<string | null>(null);

  const [networkIds, functionIds] = useMemo(() => {
    return [
      predicates.find((p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK])?.value.value,
      predicates.find((p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION])?.value.value,
    ];
  }, [predicates]);

  const payload = useMemo(() => ({
    ...(match?.params?.filter ? { status: match.params.filter } : {}),
    ...(networkIds ? { network_ids: networkIds } : {}),
    ...(functionIds ? { function_ids: functionIds } : {}),
    ...(search && search !== '' ? { q: search } : {}),
  }), [match?.params?.filter, networkIds, functionIds, search]);

  const apiConfig = {
    useCursor: true,
    limit: 20,
    payload,
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((form: Form) => ({
          ...form,
          created_by: form.created_by ? res.meta.related.users.find((user: any) => user.id === form.created_by) : null,
        })),
      };
    },
  };


  const {
    data: forms,
    setData: setForms,
    meta: metaData,
    loading,
    refetch,
    reset,
  } = useRequest(`/v1/organisations/${organisation.id}/forms`, {
    ...apiConfig,
    onSuccess: (res: any) => {
      setMetas((_metas: any) => [..._metas, res.meta]);
    },
  });

  const limit = 1;
  const isOverLimit = metaData?.counts?.total_count >= limit;
  const isPremium = organisation.enabled_components.includes(EComponentTypes.FORMS_PREMIUM);
  const canDuplicate = isPremium || !isOverLimit;

  const openIntercom = () => window.Intercom?.('showNewMessage');

  const {
    isAvailable: canCreateForms, showUpgradeModal,
  } = useIsAvailableInPlanPackage(EPlanPackageConfig.FORMS, forms?.length);

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [payload]);

  const handleDuplicateForm = useCallback(async (form: Form) => {
    try {
      const { data: item } = await Api.post<{ data: Form }>(`/v1/organisations/${organisation.id}/forms/${form.id}/duplicate`);
      setForms((prevForms: Form[]) => [item, ...prevForms]);
      return AlertService.success(t('forms:duplicated_form'));
    } catch (error: any) {
      AlertService.forStatus(error?.status, {
        warning: t('forms:duplicate_error'),
        error: t('forms:duplicate_error'),
      });
    }
  }, [organisation.id, setForms, t]);

  const handleDeleteForm = useCallback(async (form: Form) => {
    try {
      await Api.delete(`/v1/organisations/${organisation.id}/forms/${form.id}`);
      setForms((prevForms: Form[]) => prevForms.filter((f) => f.id !== form.id));
      return AlertService.success(t('forms:deleted_form'));
    } catch (error: any) {
      AlertService.forStatus(error.status, {
        warning: t('forms:delete_form_warning'),
        error: t('forms:delete_form_error'),
      });
    }
  }, [organisation.id, setForms, t]);

  const handleArchiveForm = useCallback(async (form: Form) => {
    try {
      await Api.post(`/v1/organisations/${organisation.id}/forms/${form.id}/archive`);
      setForms((prevForms: Form[]) => prevForms.filter((f) => f.id !== form.id));
      AlertService.success(t('forms:archive_form_success'));
    } catch (error: any) {
      AlertService.forStatus(error.status, {
        warning: t('forms:archive_form_warning'),
        error: t('forms:archive_form_error'),
      });
    }
  }, [organisation.id, setForms, t]);

  const handleUnarchiveForm = useCallback(async (form: Form) => {
    try {
      await Api.post(`/v1/organisations/${organisation.id}/forms/${form.id}/unarchive`);
      setForms((prevForms: Form[]) => prevForms.filter((f) => f.id !== form.id));
      AlertService.success(t('forms:unarchive_form_success'));
    } catch (error: any) {
      AlertService.forStatus(error.status, {
        warning: t('forms:unarchive_form_warning'),
        error: t('forms:unarchive_form_error'),
      });
    }
  }, [organisation.id, setForms, t]);

  const columnOptions = {
    audiencePreviewData,
    showUpgradeModal,
    canDuplicate,
    handleDuplicateForm,
    handleDeleteForm,
    handleArchiveForm,
    handleUnarchiveForm,
    setTemplateFormId,
  };

  const meta: FormsMeta = metaData;
  const columns = useColumns(forms, meta, match?.params?.filter || '', columnOptions);

  const handleCreateForm = async (templateId?: string) => {
    if (templateId) {
      try {
        const res: any = await Api.post(`/v1/organisations/${organisation.id}/forms`, { form_template_id: templateId });
        setTemplatesOpen(false);
        history.push(`/admin/forms/${res.data.id}/edit/screens?back=${encodedPath}`);
        return AlertService.success(t('forms:created_form', { title: res.data.title }));
      } catch (response) {
        // @ts-expect-error
        return AlertService.forStatus(response.status_code, {
          warning: t('forms:create_form_warning'),
          error: t('forms:create_form_error'),
        });
      }
    }
    return history.push(`/admin/forms/create?back=${encodedPath}`);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <>
      {templateFormId && (
        <CreateTemplateForm
          formId={templateFormId}
          onClose={() => setTemplateFormId(null)}
        />
      )}

      <FormsSubNavigation meta={meta} />

      <Container name="Forms">
        <FormsTopNavigation
          handleCreateForm={handleCreateForm}
          canCreateForms={canCreateForms}
          showUpgradeModal={showUpgradeModal}
          isOverLimit={isOverLimit}
          isPremium={isPremium}
          setTemplatesOpen={setTemplatesOpen}
        />

        <Container.Content>

          {!isPremium && (isOverLimit ? (
            <Alert type="warning">
              <Trans
                i18nKey="forms:form_limit"
                values={{ count: limit }}
                components={[<a onClick={openIntercom} role="button" />]}
              />
            </Alert>
          ) : (
            <Alert type="info">
              <Trans
                i18nKey="forms:form_free_plan"
                values={{ count: limit }}
                components={[<a onClick={openIntercom} role="button" />]}
              />
            </Alert>
          ))}

          {(isPremium || !isOverLimit) && (
            <>
              <Bar>
                <h2>{t('forms:create_a_form')}</h2>
              </Bar>

              <FormTemplates
                handleCreateForm={handleCreateForm}
                canCreateForms={canCreateForms}
                showUpgradeModal={showUpgradeModal}
                setTemplatesOpen={setTemplatesOpen}
              />
            </>
          )}

          <div className="tw-h-4" />

          <Bar className="FiltersToolbarBar">
            <FiltersToolbar
              predicates={predicates}
              setPredicates={setPredicates}
              organisation={organisation}
              onSearch={handleSearch}
              searchPlaceholder={t('common:form_filter_search_placeholder')}
            />
          </Bar>

          <Overview>
            <DataTable
              columns={columns}
              data={forms}
              loading={loading}
              onShowMore={refetch}
              pagination={{
                ...(omit(apiConfig, 'payload')),
                ...meta?.pagination,
              }}
              emptyView={<Placeholder title={t('forms:no_forms_placeholder')} />}
            />
          </Overview>
        </Container.Content>

        <TemplateLibrary
          show={isTemplatesOpen}
          onCreateForm={handleCreateForm}
          onClose={() => setTemplatesOpen(false)}
        />
      </Container>
    </>
  );
};
